import type { ChecklistItem, Goal, SubTask, Task } from '../../types/timeline'
import type { TimelineBoardKey } from '../../types/schema'
import type { ClipboardEntry, DetailedSelection, PasteTarget } from './types'

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `cb-${Date.now()}-${Math.random().toString(16).slice(2)}`

/** Deep clone a checklist item with a fresh id. */
const cloneChecklistItem = (item: ChecklistItem): ChecklistItem => ({
  ...item,
  id: newId(),
})

/** Deep clone a subtask + its checklist with fresh ids end-to-end. */
const cloneSubTask = (subTask: SubTask): SubTask => ({
  ...subTask,
  id: newId(),
  checklist: subTask.checklist.map(cloneChecklistItem),
})

/** Deep clone a task + subtasks + checklists with fresh ids. */
const cloneTask = (task: Task): Task => ({
  ...task,
  id: newId(),
  checklist: task.checklist.map(cloneChecklistItem),
  subTasks: task.subTasks.map(cloneSubTask),
})

/** Deep clone a goal + tasks + subtasks + checklists with fresh ids. */
const cloneGoal = (goal: Goal): Goal => ({
  ...goal,
  id: newId(),
  checklist: goal.checklist.map(cloneChecklistItem),
  tasks: goal.tasks.map(cloneTask),
})

// ---------------------------------------------------------------------------
// Resolvers — pluck the selected item out of the goals list so the panel can
// stash it on the clipboard.
// ---------------------------------------------------------------------------

export const resolveClipboardEntry = (
  selection: DetailedSelection,
  goals: Goal[],
  op: 'copy' | 'cut',
  sourceBoard: TimelineBoardKey,
): ClipboardEntry | null => {
  const goal = goals.find((candidate) => candidate.id === selection.goalId)
  if (!goal) return null

  if (selection.kind === 'goal') {
    return {
      op,
      kind: 'goal',
      payload: goal,
      sourceBoard,
    }
  }

  if (selection.kind === 'task') {
    const task = goal.tasks.find((candidate) => candidate.id === selection.taskId)
    if (!task) return null
    return {
      op,
      kind: 'task',
      payload: task,
      sourceGoalId: goal.id,
      sourceBoard,
    }
  }

  if (selection.kind === 'subtask') {
    const task = goal.tasks.find((candidate) => candidate.id === selection.taskId)
    const sub = task?.subTasks.find((candidate) => candidate.id === selection.subTaskId)
    if (!task || !sub) return null
    return {
      op,
      kind: 'subtask',
      payload: sub,
      sourceGoalId: goal.id,
      sourceTaskId: task.id,
      sourceBoard,
    }
  }

  if (selection.kind === 'checklist') {
    const findChecklist = (): ChecklistItem | null => {
      if (!selection.taskId) {
        return goal.checklist.find((candidate) => candidate.id === selection.checklistId) ?? null
      }
      const task = goal.tasks.find((candidate) => candidate.id === selection.taskId)
      if (!task) return null
      if (!selection.subTaskId) {
        return task.checklist.find((candidate) => candidate.id === selection.checklistId) ?? null
      }
      const sub = task.subTasks.find((candidate) => candidate.id === selection.subTaskId)
      return sub?.checklist.find((candidate) => candidate.id === selection.checklistId) ?? null
    }
    const item = findChecklist()
    if (!item) return null
    return {
      op,
      kind: 'checklist',
      payload: item,
      sourceGoalId: goal.id,
      sourceTaskId: selection.taskId ?? null,
      sourceSubTaskId: selection.subTaskId ?? null,
      sourceBoard,
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// Paste eligibility — given a clipboard entry and a target (selection or
// board folder), returns true if it's hierarchy-compatible.
// ---------------------------------------------------------------------------

export const canPasteAt = (
  entry: ClipboardEntry,
  target: PasteTarget | DetailedSelection | null,
): boolean => {
  if (!target) return false
  // Back-compat: callers that still pass a bare selection get wrapped.
  const normalized: PasteTarget =
    'kind' in target && (target.kind === 'selection' || target.kind === 'board')
      ? (target as PasteTarget)
      : { kind: 'selection', selection: target as DetailedSelection }

  if (entry.kind === 'goal') {
    // Goals paste at a board folder root only.
    return normalized.kind === 'board'
  }
  if (normalized.kind === 'board') return false
  const selection = normalized.selection
  if (entry.kind === 'task') {
    return selection.kind === 'goal'
  }
  if (entry.kind === 'subtask') {
    return selection.kind === 'task'
  }
  // Checklists paste under goal/task/subtask.
  return (
    selection.kind === 'goal' || selection.kind === 'task' || selection.kind === 'subtask'
  )
}

// ---------------------------------------------------------------------------
// Apply paste — returns updated source + destination goals + the new selection
// pointing at the pasted item.
// ---------------------------------------------------------------------------

const removeFromGoal = (goal: Goal, entry: ClipboardEntry): Goal => {
  if (entry.kind === 'goal') return goal // handled at the array level
  if (entry.kind === 'task') {
    return { ...goal, tasks: goal.tasks.filter((task) => task.id !== entry.payload.id) }
  }
  if (entry.kind === 'subtask') {
    return {
      ...goal,
      tasks: goal.tasks.map((task) =>
        task.id === entry.sourceTaskId
          ? { ...task, subTasks: task.subTasks.filter((sub) => sub.id !== entry.payload.id) }
          : task,
      ),
    }
  }
  // checklist
  const removeFromList = (items: ChecklistItem[]) =>
    items.filter((item) => item.id !== entry.payload.id)
  if (!entry.sourceTaskId) {
    return { ...goal, checklist: removeFromList(goal.checklist) }
  }
  return {
    ...goal,
    tasks: goal.tasks.map((task) => {
      if (task.id !== entry.sourceTaskId) return task
      if (!entry.sourceSubTaskId) return { ...task, checklist: removeFromList(task.checklist) }
      return {
        ...task,
        subTasks: task.subTasks.map((sub) =>
          sub.id === entry.sourceSubTaskId ? { ...sub, checklist: removeFromList(sub.checklist) } : sub,
        ),
      }
    }),
  }
}

/**
 * Build the payload-to-be-pasted: for `cut` we re-use the original payload
 * (ids preserved → move semantics); for `copy` we deep-clone with fresh ids.
 */
const buildPastedItem = (entry: ClipboardEntry): ClipboardEntry['payload'] => {
  if (entry.op === 'cut') return entry.payload
  if (entry.kind === 'goal') return cloneGoal(entry.payload as Goal)
  if (entry.kind === 'task') return cloneTask(entry.payload as Task)
  if (entry.kind === 'subtask') return cloneSubTask(entry.payload as SubTask)
  return cloneChecklistItem(entry.payload as ChecklistItem)
}

const insertIntoGoal = (
  goal: Goal,
  entry: ClipboardEntry,
  destinationSelection: DetailedSelection,
  pasted: ClipboardEntry['payload'],
): Goal => {
  if (entry.kind === 'task') {
    return { ...goal, tasks: [...goal.tasks, pasted as Task] }
  }
  if (entry.kind === 'subtask') {
    if (destinationSelection.kind !== 'task') return goal
    return {
      ...goal,
      tasks: goal.tasks.map((task) =>
        task.id === destinationSelection.taskId
          ? { ...task, subTasks: [...task.subTasks, pasted as SubTask] }
          : task,
      ),
    }
  }
  if (entry.kind === 'checklist') {
    const checklistItem = pasted as ChecklistItem
    if (destinationSelection.kind === 'goal') {
      return { ...goal, checklist: [...goal.checklist, checklistItem] }
    }
    if (destinationSelection.kind === 'task') {
      return {
        ...goal,
        tasks: goal.tasks.map((task) =>
          task.id === destinationSelection.taskId
            ? { ...task, checklist: [...task.checklist, checklistItem] }
            : task,
        ),
      }
    }
    if (destinationSelection.kind === 'subtask') {
      return {
        ...goal,
        tasks: goal.tasks.map((task) =>
          task.id === destinationSelection.taskId
            ? {
                ...task,
                subTasks: task.subTasks.map((sub) =>
                  sub.id === destinationSelection.subTaskId
                    ? { ...sub, checklist: [...sub.checklist, checklistItem] }
                    : sub,
                ),
              }
            : task,
        ),
      }
    }
  }
  return goal
}

interface PasteResult {
  nextGoals: Goal[]
  pastedSelection: DetailedSelection
  pastedItemId: string
  /** Every node id introduced (or moved) by this paste — the root id plus
   * every descendant. Used to mark dirty + draft-attention in bulk. */
  newIds: Set<string>
  /** True when cut + cross-goal — caller should mark the source goal dirty too. */
  movedAcrossGoals: boolean
  /** Board the pasted item now lives on. Used by the caller to stamp the new
   * element's `board` field and to update the goal-to-board map. */
  targetBoard: TimelineBoardKey
  /** True when this paste changed the board of an existing element (cross-board
   * cut OR copy landed on a different board than source). */
  movedAcrossBoards: boolean
}

const collectDescendantIds = (payload: ClipboardEntry['payload']): Set<string> => {
  const ids = new Set<string>()
  const p = payload as { id: string; tasks?: Task[]; subTasks?: SubTask[] }
  ids.add(p.id)
  if (Array.isArray(p.tasks)) {
    for (const task of p.tasks) {
      ids.add(task.id)
      for (const sub of task.subTasks) ids.add(sub.id)
    }
  } else if (Array.isArray(p.subTasks)) {
    for (const sub of p.subTasks) ids.add(sub.id)
  }
  return ids
}

export const applyPaste = (
  goals: Goal[],
  entry: ClipboardEntry,
  target: PasteTarget,
  /** Map of goalId -> board (the caller maintains this from TimelineContext). */
  goalIdToBoard: Map<string, TimelineBoardKey>,
): PasteResult | null => {
  if (!canPasteAt(entry, target)) return null

  // --- Goal paste -- board folder target only. -----------------------------
  if (entry.kind === 'goal' && target.kind === 'board') {
    const targetBoard = target.board
    const pasted = buildPastedItem(entry) as Goal
    let nextGoals: Goal[]
    if (entry.op === 'cut') {
      // Move the existing goal — keep ids, just re-board it. The goal is
      // already in `goals`; the array stays the same. The board change is
      // surfaced in the result and the caller updates the map + Firestore.
      nextGoals = goals
    } else {
      // Copy — append a fresh-id clone of the goal.
      nextGoals = [...goals, pasted]
    }
    return {
      nextGoals,
      pastedSelection: { kind: 'goal', goalId: pasted.id },
      pastedItemId: pasted.id,
      newIds: collectDescendantIds(pasted),
      movedAcrossGoals: false,
      targetBoard,
      movedAcrossBoards: entry.sourceBoard !== targetBoard,
    }
  }

  if (target.kind !== 'selection') return null
  const destinationSelection = target.selection
  const destinationGoalId = destinationSelection.goalId
  const targetBoard = goalIdToBoard.get(destinationGoalId) ?? entry.sourceBoard
  const sourceGoalId = entry.kind === 'goal' ? entry.payload.id : entry.sourceGoalId
  const movedAcrossGoals = entry.op === 'cut' && sourceGoalId !== destinationGoalId
  const pasted = buildPastedItem(entry)

  const updatedGoals = goals.map((goal) => {
    // Source: remove the original (cut only — copy leaves the source alone).
    if (entry.op === 'cut' && entry.kind !== 'goal' && goal.id === sourceGoalId) {
      goal = removeFromGoal(goal, entry)
    }
    // Destination: insert the pasted item.
    if (goal.id === destinationGoalId) {
      goal = insertIntoGoal(goal, entry, destinationSelection, pasted)
    }
    return goal
  })

  // Compute the new selection pointing at the pasted item.
  let pastedSelection: DetailedSelection
  if (entry.kind === 'task') {
    pastedSelection = {
      kind: 'task',
      goalId: destinationGoalId,
      taskId: (pasted as Task).id,
    }
  } else if (entry.kind === 'subtask') {
    pastedSelection = {
      kind: 'subtask',
      goalId: destinationGoalId,
      taskId: (destinationSelection as { taskId: string }).taskId,
      subTaskId: (pasted as SubTask).id,
    }
  } else if (entry.kind === 'checklist') {
    pastedSelection = {
      kind: 'checklist',
      goalId: destinationGoalId,
      taskId: 'taskId' in destinationSelection ? (destinationSelection as { taskId?: string }).taskId ?? null : null,
      subTaskId:
        'subTaskId' in destinationSelection
          ? (destinationSelection as { subTaskId?: string }).subTaskId ?? null
          : null,
      checklistId: (pasted as ChecklistItem).id,
    }
  } else {
    // unreachable (goal handled above) — keep TS happy.
    pastedSelection = destinationSelection
  }

  return {
    nextGoals: updatedGoals,
    pastedSelection,
    pastedItemId: (pasted as { id: string }).id,
    newIds: collectDescendantIds(pasted),
    movedAcrossGoals,
    targetBoard,
    movedAcrossBoards: entry.sourceBoard !== targetBoard,
  }
}
